"use client";
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useKrpano } from '@/contexts/KrpanoProvider';
import hotspotServices from '@/templates/Admin/variants/default/utils/services/hotspot';
import type { APIHotspot } from '@/types/krpano';
import { useIdPath } from "@/contexts/IdPathProvider";
import { applyNavigationHotspotScale, navigationHotspotRenderConfig, NAVIGATION_HOTSPOT_SCALE_APPLIED, type NavigationHotspotScaleAppliedDetail } from '@/utils/navigation-hotspot-scale';
import { getSvgIconById } from '../variants/default/features/navigation3D/components/sections/SvgIconPicker';
import { ensureHotspotStyle, registerAllHotspotTypeStyles } from '../utils/krpano-dynamic-hotspot-style';

const parseConfigSection = (section: any) => {
    if (!section) return undefined;
    if (typeof section === 'string') {
        try {
            return JSON.parse(section);
        } catch (error) {
            console.warn('Failed to parse config section:', error);
            return undefined;
        }
    }
    return section;
};

const isValueDistorted = (val: any): boolean => {
    if (val === true || val === 1 || val === '1') return true;
    if (typeof val === 'string' && val.trim().toLowerCase() === 'true') return true;
    return false;
};

export const normalizeHotspotConfig = (rawConfig: any): Record<string, any> => {
    let base: Record<string, any> = {};

    if (!rawConfig) {
        return base;
    }

    if (typeof rawConfig === 'string') {
        try {
            base = JSON.parse(rawConfig);
        } catch (error) {
            console.warn('Failed to parse hotspot config JSON:', error);
            return {};
        }
    } else if (typeof rawConfig === 'object') {
        base = { ...rawConfig };
    }

    const normalized: Record<string, any> = { ...base };

    normalized.Title = parseConfigSection(base.Title) || parseConfigSection(base['title-hotspot']) || normalized.Title;
    normalized.Thumbnail = parseConfigSection(base.Thumbnail) || parseConfigSection(base['thumbnail-hotspot']) || normalized.Thumbnail;
    normalized['Call-out'] = parseConfigSection(base['Call-out']) || parseConfigSection(base['callout-hotspot']) || normalized['Call-out'];

    return normalized;
};

export const convertApiColorToHex = (color?: string) => {
    if (!color) return undefined;
    if (color.startsWith('rgba') || color.startsWith('rgb')) {
        return color;
    }
    if (color.startsWith('0x')) {
        return `#${color.slice(2)}`;
    }
    if (!color.startsWith('#')) {
        return `#${color}`;
    }
    return color;
};

export const convertHexToRgba = (color?: string, opacity?: number) => {
    const normalized = convertApiColorToHex(color);
    if (!normalized || normalized.startsWith('rgb')) {
        return normalized || undefined;
    }

    const hex = normalized.replace('#', '');
    if (hex.length !== 6) {
        return normalized;
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const alpha = typeof opacity === 'number' ? Math.min(Math.max(opacity, 0), 1) : 1;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const buildTitleCss = (hotspot: Partial<APIHotspot>, config: Record<string, any>, isCallout: boolean = false) => {
    const titleConfig = config?.Title || {};
    const resolvedTextColor = hotspot.title_color || titleConfig.color || config?.color || '#000000ff';
    const resolvedBgColor = titleConfig['background-color'] || '#FFFFFF';
    const textColor = convertHexToRgba(resolvedTextColor, titleConfig['color-opacity']) || 'rgba(0, 0, 0, 1)';
    const backgroundColor = convertHexToRgba(resolvedBgColor, titleConfig['background-opacity']) || 'rgba(255, 255, 255, 1)';
    const fontFamily = titleConfig['font-family'] || 'var(--font-family-project)';
    const fontSize = titleConfig['font-size'] || '14px';
    const fontWeight = titleConfig['font-weight'] || 'normal';
    const textAlign = titleConfig['text-align'] || 'center';
    const padding = titleConfig.padding || '12px';
    const borderRadius = titleConfig['border-radius'] || '8px';
    const border = titleConfig.border || 'solid 1px #F6E493';
    const minWidth = titleConfig['min-width'] || '0px';

    const hasTitleConfig = Object.keys(titleConfig).length > 0;
    const width = titleConfig.width || (isCallout && !hasTitleConfig ? '140px' : 'auto');

    // Resolve max-width: ensure it doesn't constrain width when width is explicitly set
    // If width is set and larger than max-width, use width as max-width
    let maxWidth = titleConfig['max-width'] || '500px';
    if (width && width !== 'auto') {
        const widthNum = parseFloat(width);
        const maxWidthNum = parseFloat(maxWidth);
        if (!isNaN(widthNum) && !isNaN(maxWidthNum) && widthNum > maxWidthNum) {
            // Width is larger than max-width, so max-width should not constrain
            maxWidth = width;
        }
    }

    const height = titleConfig.height || 'auto';
    const wordWrap = titleConfig['word-wrap'] || 'break-word';
    const overflowWrap = titleConfig['overflow-wrap'] || 'break-word';
    const whiteSpace = titleConfig['white-space'] || 'normal';
    const lineHeight = titleConfig['line-height'] || '1.2';
    return `color:${textColor};
                font-family:${fontFamily};
                font-size:${fontSize};
                font-weight:${fontWeight};
                text-align:${textAlign};
                background-color:${backgroundColor};
                padding:${padding};
                height:${height};
                width:${width};
                min-width:${minWidth};
                max-width:${maxWidth};
                border-radius:${borderRadius};
                border:${border};
                word-wrap:${wordWrap};
                overflow-wrap:${overflowWrap};
                white-space:${whiteSpace};
                line-height:${lineHeight};`;
};


interface HotspotManagerContextType {
    hotspots: APIHotspot[];
    currentSceneHotspots: APIHotspot[];
    loadAllHotspots: () => Promise<void>;
    loadHotspotsByScene: (sceneName: string) => Promise<void>;
    addHotspotToScene: (hotspot: APIHotspot) => void;
    removeHotspotFromScene: (hotspotId: number) => void;
    updateHotspotInKrpano: (hotspot: APIHotspot) => void;
    refreshHotspots: () => Promise<void>;
    isLoading: boolean;
    // Popup state
    popupHotspot: APIHotspot | null;
    popupPosition: { x: number; y: number } | null;
    isPopupVisible: boolean;
    showPopup: (hotspot: APIHotspot, position: { x: number; y: number }) => void;
    hidePopup: () => void;
    // Update form state
    openUpdateForm: (hotspot: APIHotspot) => void;
    updateFormHotspot: APIHotspot | null;
    isUpdateFormOpen: boolean;
    closeUpdateForm: () => void;
}

const HotspotManagerContext = createContext<HotspotManagerContextType | undefined>(undefined);

export const HotspotManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { krpano, krpanoCurrent } = useKrpano();
    const [hotspots, setHotspots] = useState<APIHotspot[]>([]);
    const [currentSceneHotspots, setCurrentSceneHotspots] = useState<APIHotspot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastScene, setLastScene] = useState<string>('');
    const [lastLoadedIdPath, setLastLoadedIdPath] = useState<string>('');
    const scaleOnlyHotspots = useRef<APIHotspot[] | null>(null);
    const sceneRenderPending = useRef(false);

    // Popup state
    const [popupHotspot, setPopupHotspot] = useState<APIHotspot | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    // Update form state
    const [updateFormHotspot, setUpdateFormHotspot] = useState<APIHotspot | null>(null);
    const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
    const { idPath } = useIdPath();

    const parseConfigSection = (section: any) => {
        if (!section) return undefined;
        if (typeof section === 'string') {
            try {
                return JSON.parse(section);
            } catch (error) {
                console.warn('Failed to parse config section:', error);
                return undefined;
            }
        }
        return section;
    };

    const normalizeHotspotConfig = (rawConfig: any): Record<string, any> => {
        let base: Record<string, any> = {};

        if (!rawConfig) {
            return base;
        }

        if (typeof rawConfig === 'string') {
            try {
                base = JSON.parse(rawConfig);
            } catch (error) {
                console.warn('Failed to parse hotspot config JSON:', error);
                return {};
            }
        } else if (typeof rawConfig === 'object') {
            base = { ...rawConfig };
        }

        const normalized: Record<string, any> = { ...base };

        normalized.Title = parseConfigSection(base.Title) || parseConfigSection(base['title-hotspot']) || normalized.Title;
        normalized.Thumbnail = parseConfigSection(base.Thumbnail) || parseConfigSection(base['thumbnail-hotspot']) || normalized.Thumbnail;
        normalized['Call-out'] = parseConfigSection(base['Call-out']) || parseConfigSection(base['callout-hotspot']) || normalized['Call-out'];

        return normalized;
    };

    const convertApiColorToHex = (color?: string) => {
        if (!color) return undefined;
        if (color.startsWith('rgba') || color.startsWith('rgb')) {
            return color;
        }
        if (color.startsWith('0x')) {
            return `#${color.slice(2)}`;
        }
        if (!color.startsWith('#')) {
            return `#${color}`;
        }
        return color;
    };

    const convertHexToRgba = (color?: string, opacity?: number) => {
        const normalized = convertApiColorToHex(color);
        if (!normalized || normalized.startsWith('rgb')) {
            return normalized || undefined;
        }

        const hex = normalized.replace('#', '');
        if (hex.length !== 6) {
            return normalized;
        }

        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const alpha = typeof opacity === 'number' ? Math.min(Math.max(opacity, 0), 1) : 1;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const buildTitleCss = (hotspot: APIHotspot, config: Record<string, any>, isCallout: boolean = false) => {
        const titleConfig = config?.Title || {};
        const resolvedTextColor = hotspot.title_color || titleConfig.color || config?.color || '#000000ff';
        const resolvedBgColor = titleConfig['background-color'] || '#FFFFFF';
        const textColor = convertHexToRgba(resolvedTextColor, titleConfig['color-opacity']) || 'rgba(0, 0, 0, 1)';
        const backgroundColor = convertHexToRgba(resolvedBgColor, titleConfig['background-opacity']) || 'rgba(255, 255, 255, 1)';
        const fontFamily = titleConfig['font-family'] || 'var(--font-family-project)';
        const fontSize = titleConfig['font-size'] || '14px';
        const fontWeight = titleConfig['font-weight'] || 'normal';
        const textAlign = titleConfig['text-align'] || 'center';
        const padding = titleConfig.padding || '12px';
        const borderRadius = titleConfig['border-radius'] || '8px';
        const border = titleConfig.border || 'solid 1px #F6E493';
        const maxWidth = titleConfig['max-width'] || '200px';
        const minWidth = titleConfig['min-width'] || '0px';

        const hasTitleConfig = Object.keys(titleConfig).length > 0;
        const width = titleConfig.width || (isCallout && !hasTitleConfig ? '140px' : 'auto');

        const height = titleConfig.height || 'auto';
        const wordWrap = titleConfig['word-wrap'] || 'break-word';
        const overflowWrap = titleConfig['overflow-wrap'] || 'break-word';
        const whiteSpace = titleConfig['white-space'] || 'normal';
        const lineHeight = titleConfig['line-height'] || '1.2';
        const fontStyle = titleConfig['font-style'] || 'normal';
        return `color:${textColor};
                font-family:${fontFamily};
                font-size:${fontSize};
                font-weight:${fontWeight};
                font-style:${fontStyle};
                text-align:${textAlign};
                background-color:${backgroundColor};
                padding:${padding};
                height:${height};
                width:${width};
                min-width:${minWidth};
                max-width:${maxWidth};
                border-radius:${borderRadius};
                border:${border};
                word-wrap:${wordWrap};
                overflow-wrap:${overflowWrap};
                white-space:${whiteSpace};
                line-height:${lineHeight};`;
    };

    type TextMeasureFn = (text: string, font?: string, maxWidth?: number) => boolean;
    type TitleOverlaySettings = Record<string, string | number | boolean | undefined>;

    const clamp01 = (value?: number) => {
        if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
        return Math.min(Math.max(value, 0), 1);
    };

    const parseNumericValue = (input?: string | number, fallback?: number) => {
        if (typeof input === 'number' && !Number.isNaN(input)) {
            return input;
        }
        if (typeof input === 'string') {
            const match = input.match(/-?\d+(\.\d+)?/);
            if (match) {
                return parseFloat(match[0]);
            }
        }
        return fallback;
    };

    const parseBorderValue = (borderValue?: string) => {
        if (!borderValue || borderValue.toLowerCase() === 'none') {
            return { width: 0, color: undefined };
        }

        const widthMatch = borderValue.match(/([\d.]+)\s*px/);
        const colorMatch = borderValue.match(/(#[0-9a-fA-F]{3,8}|0x[0-9a-fA-F]{6}|rgba?\([^\)]+\))/);

        return {
            width: widthMatch ? parseFloat(widthMatch[1]) : undefined,
            color: colorMatch ? colorMatch[1] : undefined
        };
    };

    const rgbChannelToHex = (channel: number) => channel.toString(16).padStart(2, '0');

    const convertCssColorToKrpanoHex = (color?: string) => {
        if (!color) return undefined;
        if (color.startsWith('0x')) {
            return color;
        }
        if (color.startsWith('#')) {
            return `0x${color.slice(1)}`;
        }
        if (color.startsWith('rgb')) {
            const matches = color.match(/\d+/g);
            if (matches && matches.length >= 3) {
                const [r, g, b] = matches.map(Number).slice(0, 3);
                return `0x${rgbChannelToHex(r)}${rgbChannelToHex(g)}${rgbChannelToHex(b)}`;
            }
        }
        return undefined;
    };

    // Robust scale setter to avoid race conditions when loadstyle or plugin loading resets the scale.
    const setHotspotScale = useCallback((hotspotName: string, value?: number) => {
        if (!krpano || value === undefined || Number.isNaN(value)) return;
        try {
            krpano.call(`delayedcall(0.02, set(hotspot[${hotspotName}].scale, ${value}); tween(hotspot[${hotspotName}].scale, ${value}, 0.0, default, 0.1)); delayedcall(0.6, set(hotspot[${hotspotName}].scale, ${value}));`);
        } catch (err) {
            try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* noop */ }
            setTimeout(() => { try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* noop */ } }, 700);
        }
        try {
            const existingOnloaded = krpano.get(`hotspot[${hotspotName}].onloaded`);
            const appendCmd = `set(hotspot[${hotspotName}].scale, ${value});`;
            krpano.set(`hotspot[${hotspotName}].onloaded`, `${existingOnloaded || ''}${existingOnloaded ? '; ' : ''}${appendCmd}`);
        } catch (e) { /* no-op */ }
    }, [krpano]);

    useEffect(() => {
        const onScaleApplied = (event: Event) => {
            const detail = (event as CustomEvent<NavigationHotspotScaleAppliedDetail>).detail;
            if (!detail || !idPath || detail.idPath !== idPath) return;
            const updated = applyNavigationHotspotScale(hotspots, idPath, detail);
            if (updated === hotspots) return;
            scaleOnlyHotspots.current = updated;
            setHotspots(updated);
            setCurrentSceneHotspots(previous => applyNavigationHotspotScale(previous, idPath, detail));
            if (!krpano) return;
            const scale = navigationHotspotRenderConfig({ ...detail }, krpano.get('device.mobile')).scale;
            hotspots.filter(hotspot => hotspot.style === detail.style).forEach(hotspot => {
                // Patch loaded icons only; rebuilding would discard unrelated unsaved positions.
                for (const name of [`api_${hotspot.id}`, `api_${hotspot.id}_svg`]) {
                    try {
                        if (krpano.get(`hotspot[${name}].name`)) setHotspotScale(name, scale);
                    } catch { /* Instance may not be loaded in the current scene. */ }
                }
            });
        };
        window.addEventListener(NAVIGATION_HOTSPOT_SCALE_APPLIED, onScaleApplied);
        return () => window.removeEventListener(NAVIGATION_HOTSPOT_SCALE_APPLIED, onScaleApplied);
    }, [idPath, hotspots, krpano, setHotspotScale]);

    const formatDimensionForKrpano = (value?: string | number) => {
        if (value === undefined || value === null) return 'auto';
        if (value === 'auto') return 'auto';
        if (typeof value === 'number' && !Number.isNaN(value)) {
            return value.toString();
        }

        const numeric = parseNumericValue(value as string);
        return numeric !== undefined ? numeric.toString() : 'auto';
    };

    const shouldWrapText = (titleConfig: Record<string, any>) => {
        const whiteSpace = (titleConfig['white-space'] || '').toLowerCase();
        const wordWrap = (titleConfig['word-wrap'] || '').toLowerCase();
        if (whiteSpace === 'nowrap' || wordWrap === 'normal') {
            return 'false';
        }
        return 'true';
    };

    const getTitleOverlaySettings = (
        hotspot: APIHotspot,
        config: Record<string, any>,
        measureTextWidth: TextMeasureFn,
        isCallout: boolean = false
    ): TitleOverlaySettings => {
        const titleConfig = config?.Title || {};
        const hasTitleConfig = Object.keys(titleConfig).length > 0;

        // Set default values khi không có config - giống như trang sản phẩm
        const defaultBgColor = '#FFFFFF';
        const defaultBorder = 'solid 1px #F6E493';

        const backgroundOpacity = clamp01(parseNumericValue(titleConfig['background-opacity'], 1));
        const borderInfo = parseBorderValue(titleConfig.border || (!hasTitleConfig ? defaultBorder : undefined));
        const borderRadius = parseNumericValue(titleConfig['border-radius'], 8);

        const widthSetting = (titleConfig.width || (isCallout && !hasTitleConfig ? '140' : 'auto')) as string;

        const maxWidthPx = parseNumericValue(titleConfig['max-width'], 200) ?? 200;
        const minWidthPx = parseNumericValue(titleConfig['min-width'], 0) ?? 0;
        const fontFamily = titleConfig['font-family'] || 'var(--font-family-project)';
        const fontSize = titleConfig['font-size'] || '14px';

        // Đo chiều rộng thực tế của text
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        let textWidth = 0;
        if (context) {
            context.font = `${fontSize} ${fontFamily}`;
            textWidth = context.measureText(hotspot.title || '').width;
        }

        let resolvedWidth = 'auto';
        if (widthSetting !== 'auto') {
            resolvedWidth = formatDimensionForKrpano(widthSetting);
        } else {
            if (textWidth > maxWidthPx) {
                resolvedWidth = formatDimensionForKrpano(maxWidthPx);
            } else {
                resolvedWidth = 'auto';
            }
        }

        // Sử dụng giá trị mặc định khi không có config
        const backgroundColorValue = titleConfig['background-color'] || defaultBgColor;
        const backgroundColor = backgroundColorValue.toLowerCase();

        // Kiểm tra nếu background color có opacity = 0 (trong suốt hoàn toàn)
        // Ví dụ: rgba(255, 255, 255, 0) hoặc hsla(0, 0%, 100%, 0)
        const isTransparentRgba = backgroundColor.startsWith('rgba') && backgroundColor.match(/,\s*0\s*\)$/);
        const isTransparentHsla = backgroundColor.startsWith('hsla') && backgroundColor.match(/,\s*0\s*\)$/);
        const isFullyTransparent = isTransparentRgba || isTransparentHsla;

        const hasBackground = backgroundColor
            && backgroundColor !== 'transparent'
            && backgroundColor !== 'none'
            && !isFullyTransparent;

        return {
            background: hasBackground ? 'true' : 'false',
            bg: hasBackground ? 'true' : 'false', // Krpano textfield.swf cũng có thuộc tính bg riêng
            backgroundalpha: hasBackground ? (backgroundOpacity !== undefined ? backgroundOpacity.toString() : '1') : '0',
            backgroundcolor: hasBackground ? convertCssColorToKrpanoHex(backgroundColorValue) : undefined,
            bgroundedge: borderRadius !== undefined ? borderRadius.toString() : undefined,
            border: borderInfo.width && borderInfo.width > 0 ? 'true' : 'false',
            bordercolor: borderInfo.color ? convertCssColorToKrpanoHex(borderInfo.color) : undefined,
            borderwidth: borderInfo.width && borderInfo.width > 0 ? borderInfo.width.toString() : undefined,
            roundedge: borderRadius !== undefined ? borderRadius.toString() : undefined,
            width: resolvedWidth,
            wordwrap: shouldWrapText(titleConfig)
        };
    };

    const applyTitleOverlaySettings = (
        target: any,
        hotspotName: string,
        overlaySettings: TitleOverlaySettings
    ) => {
        if (!target || !overlaySettings) return;
        Object.entries(overlaySettings).forEach(([property, value]) => {
            if (value === undefined || value === null) return;
            target.set(`hotspot[${hotspotName}_invisible].${property}`, value);
        });
    };

    // Load all hotspots from API
    const loadAllHotspots = async () => {
        setIsLoading(true);
        try {
            await registerAllHotspotTypeStyles(krpano);
            const response: any = await hotspotServices.getAllHotspots(idPath);

            let hotspotList: APIHotspot[] = [];

            // Handle different response structures
            if (response && response.data) {
                if (response.data.success && response.data.data) {
                    hotspotList = Array.isArray(response.data.data) ? response.data.data : [];
                } else if (Array.isArray(response.data)) {
                    hotspotList = response.data;
                } else {
                    hotspotList = [];
                }
            }

            setHotspots(hotspotList);

        } catch (error) {
            setHotspots([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Load hotspots by scene from API
    const loadHotspotsByScene = async (sceneName: string) => {
        if (!sceneName) return;

        setIsLoading(true);
        try {
            await registerAllHotspotTypeStyles(krpano);
            const response: any = await hotspotServices.getHotspotsBySceneName({ scene_name: sceneName, id_path: idPath });

            let hotspotList: APIHotspot[] = [];

            // Handle different response structures
            if (response && response.data) {
                if (response.data.success && response.data.data) {
                    hotspotList = Array.isArray(response.data.data) ? response.data.data : [];

                } else if (Array.isArray(response.data)) {
                    hotspotList = response.data;
                } else {
                    hotspotList = [];
                }
            }

            setHotspots(hotspotList);

        } catch (error) {
            console.error('Error loading hotspots for scene:', sceneName, error);
            setHotspots([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Add hotspots for current scene when scene changes (similar to hotspot_onnewpano)
    const addHotspotsForCurrentScene = (currentSceneName: string) => {
        if (!krpano || !Array.isArray(hotspots) || hotspots.length === 0) {
            return;
        }

        // Remove only API-created hotspots from current scene to avoid conflicts
        try {
            // First, get all hotspot names in krpano
            const hotspotCount = Number(krpano.get("hotspot.count") || 0);
            if (hotspotCount > 0) {
                // IMPORTANT: Loop backwards because removing an element shifts the indices
                for (let i = hotspotCount - 1; i >= 0; i--) {
                    try {
                        const hotspotName = String(krpano.get(`hotspot[${i}].name`) || '');
                        // Remove only hotspots with api_ prefix (our API hotspots)
                        if (hotspotName && hotspotName.startsWith('api_')) {
                            krpano.call(`removehotspot(${hotspotName})`);
                        }
                    } catch (e) {
                        // Ignore errors for individual hotspots
                    }
                }
            }
        } catch (error) {
            console.warn('Error removing API hotspots:', error);
        }

        // Use all hotspots since they are already filtered by scene from API
        const sceneHotspots = hotspots;


        sceneHotspots.forEach((hotspot) => {

            // Use api_ prefix to avoid conflict with XML hotspots
            const hotspotName = `api_${hotspot.id}`;

            try {
                // Add hotspot to krpano
                krpano.call(`addhotspot(${hotspotName})`);

                // Load style based on hotspot.style (similar to original switch logic)
                const styleMapping: Record<string, string> = {
                    'default': 'hotspot_giotnuochinhtron_style',
                    'door': 'hotspot_giotnuochinhtron_style',
                    'video360gif': 'hotspot_giotnuochinhtron_style',
                    'vongtron': 'hotspot_giotnuochinhtron_style',
                    'khinhkhicau': 'hotspot_khinhkhicau_style',
                    'thienvien': 'hotspot_giotnuochinhtron_style',
                    'lotus3d': 'hotspot_giotnuochinhtron_style',
                    'chimhac': 'hotspot_giotnuochinhtron_style',
                    'daukhi': 'hotspot_giotnuochinhtron_style',
                    'svg': 'hotspot_giotnuochinhtron_style',
                    'giotnuoc': 'hotspot_giotnuochinhtron_style',
                    'giotnuochinhtron': 'hotspot_giotnuochinhtron_style',
                    'lotus': 'hotspot_giotnuochinhtron_style',
                    'location': 'hotspot_giotnuochinhtron_style',
                    'BTLS': 'hotspot_giotnuochinhtron_style',
                    'flycam': 'hotspot_giotnuochinhtron_style',
                    'video_time': 'hotspot_giotnuochinhtron_style',
                    'btpnnb': 'hotspot_giotnuochinhtron_style',
                    'image2d': 'hotspot_giotnuochinhtron_style'
                };

                let styleName = styleMapping[hotspot.style] || 'hotspot_giotnuochinhtron_style';

                // Handle show/hide logic (similar to original if(item.showhide == 1))
                if (hotspot.is_show !== 1) {
                    // For hidden styles, try to find hidden version or fallback to default hidden
                    if (styleName.includes('_style')) {
                        styleName = styleName.replace('_style', '_hidden_style');
                    } else {
                        styleName = 'hotspot_default_hidden_style';
                    }
                }
                let config = navigationHotspotRenderConfig(normalizeHotspotConfig(hotspot.config), krpano.get('device.mobile'));

                // For custom hotspots, ensure default config values
                if (hotspot.style === 'custom') {
                    config = {
                        url: config.url || '',
                        scale: config.scale ?? 1.0,
                        height: config.height || 100,
                        ...config
                    };
                }

                // Try to load style, fallback to default if failed
                try {
                    // console.log(`Loading style ${hotspot.style} for hotspot ${hotspotName}`);
                    if (hotspot.style_hover !== 'callout') {
                        if (hotspot.style == 'custom' && config) {
                            // Check if using SVG icon
                            const svgConfig = config.svg;
                            const hasSvgId = svgConfig && svgConfig.id;
                            const isUsingSvg = Boolean(hasSvgId);

                            if (isUsingSvg) {
                                let svgCode: string | null = null;
                                if (hasSvgId) {
                                    svgCode = getSvgIconById(String(svgConfig.id));
                                }

                                if (svgCode) {
                                    // SVG mode: hide main hotspot icon, show SVG frame instead
                                    krpano.set(`hotspot[${hotspotName}].visible`, false);
                                    krpano.set(`hotspot[${hotspotName}].enabled`, false);

                                    // Create SVG hotspot
                                    const svgHotspotName = `${hotspotName}_svg`;

                                    const svgColor = svgConfig.color || '#000000';
                                    const svgBgColor = svgConfig.bgColor || '#ffffff';
                                    const svgBorderRadius = svgConfig.borderRadius || 8;
                                    const svgPadding = svgConfig.padding !== undefined ? svgConfig.padding : 8;
                                    const svgScale = config.scale ?? 1;

                                    // Build SVG icon with custom colors
                                    const coloredSvg = svgCode.replace(/currentColor/g, svgColor);

                                    // Create HTML frame with SVG icon
                                    const frameHtml = `
                                    <div style="
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        width: 60px;
                                        height: 60px;
                                        padding: ${svgPadding}px;
                                        background-color: ${svgBgColor};
                                        border-radius: ${svgBorderRadius}px;
                                        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                                        box-sizing: border-box;
                                    ">
                                        <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: ${svgColor};">
                                            ${coloredSvg}
                                        </div>
                                    </div>
                                `;

                                    const svgCss = `
                                    background: transparent !important;
                                    border: none !important;
                                    padding: 0 !important;
                                    margin: 0 !important;
                                `;

                                    // Calculate SVG position offset
                                    const svgOy = -5;

                                    // Remove old SVG hotspot if exists
                                    try { krpano.call(`removehotspot(${svgHotspotName})`); } catch (e) { /* ignore */ }

                                    // Create SVG HTML hotspot
                                    krpano.call(`addhotspot(${svgHotspotName})`);
                                    krpano.set(`hotspot[${svgHotspotName}].type`, 'text');
                                    krpano.set(`hotspot[${svgHotspotName}].html`, frameHtml);
                                    krpano.set(`hotspot[${svgHotspotName}].css`, svgCss);
                                    krpano.set(`hotspot[${svgHotspotName}].ath`, hotspot.ath);
                                    krpano.set(`hotspot[${svgHotspotName}].atv`, hotspot.atv);
                                    krpano.set(`hotspot[${svgHotspotName}].oy`, svgOy);
                                    krpano.set(`hotspot[${svgHotspotName}].edge`, 'bottom');
                                    krpano.set(`hotspot[${svgHotspotName}].distorted`, false);
                                    krpano.set(`hotspot[${svgHotspotName}].scale`, svgScale);
                                    krpano.set(`hotspot[${svgHotspotName}].visible`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].enabled`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].bgcolor`, 'transparent');
                                    krpano.set(`hotspot[${svgHotspotName}].bgalpha`, 0);
                                    krpano.set(`hotspot[${svgHotspotName}].bgroundedge`, 0);
                                    krpano.set(`hotspot[${svgHotspotName}].bgborder`, 0);
                                    krpano.set(`hotspot[${svgHotspotName}].zorder`, 6);

                                    // Enable drag for SVG hotspot
                                    krpano.set(`hotspot[${svgHotspotName}].ondown`, "set(dragging,true); draghotspot();");
                                    krpano.set(`hotspot[${svgHotspotName}].onup`, `set(dragging,false); js(window.updateSvgHotspotPositionApi('${hotspot.id}'));`);

                                    // Set onclick to show popup
                                    krpano.set(`hotspot[${svgHotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);

                                } else {
                                    console.warn(`[HotspotManager] Could not load SVG for hotspot ${hotspotName} with ID ${svgConfig.id}`);
                                    // Fallback to Image mode if SVG ID lookup fails
                                    krpano.set(`hotspot[${hotspotName}].url`, config.url);
                                }
                            } else {
                                // Image mode: use URL as before
                                krpano.set(`hotspot[${hotspotName}].url`, config.url);
                                setHotspotScale(hotspotName, config.scale ?? 0.3);
                                krpano.set(`hotspot[${hotspotName}].align`, "center");
                                krpano.set(`hotspot[${hotspotName}].zorder`, 0);

                                const customHeight = typeof config.height === 'number'
                                    ? config.height
                                    : (config.height !== undefined && config.height !== null && config.height !== '' ? parseInt(String(config.height), 10) : 100);

                                if (config.distorted || customHeight === 0) {
                                    krpano.set(`hotspot[${hotspotName}].distorted`, true);
                                    krpano.set(`hotspot[${hotspotName}].rx`, config.rx !== undefined ? Number(config.rx) : -60);
                                    krpano.set(`hotspot[${hotspotName}].edge`, 'center');
                                    krpano.set(`hotspot[${hotspotName}].oy`, 0);
                                    try { krpano.call(`removehotspot(${hotspotName}_trait)`); } catch (e) { }
                                } else {
                                    krpano.set(`hotspot[${hotspotName}].oy`, `-${customHeight}`);
                                    krpano.call('addhotspot(' + hotspotName + '_trait)');
                                    krpano.set('hotspot[' + hotspotName + '_trait].url', 'https://sanpham.starglobal3d.com/managements/js/plugins/hs/trait.png');
                                    krpano.set('hotspot[' + hotspotName + '_trait].align', 'bottom');
                                    krpano.set('hotspot[' + hotspotName + '_trait].edge', 'bottom');
                                    krpano.set('hotspot[' + hotspotName + '_trait].ath', hotspot.ath);
                                    krpano.set('hotspot[' + hotspotName + '_trait].atv', hotspot.atv);
                                    krpano.set('hotspot[' + hotspotName + '_trait].height', customHeight);
                                }
                            }
                        }
                        else {
                            // Load style first
                            ensureHotspotStyle(krpano, hotspot.style);
                            krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_${hotspot.style}_style)`);

                            // Only override scale if explicitly defined in config
                            if (config.scale !== undefined && config.scale !== null) {
                                const scaleValue = config.scale;

                                // Apply robust scale setter to avoid race condition vs loadstyle
                                setHotspotScale(hotspotName, scaleValue);
                            } else {
                            }
                        }
                    } else {
                        // Callout style: TỰ TẠO hoàn toàn trong JS để đảm bảo VR compatibility
                        // Không dùng create_hs_back vì nó tạo layer text (không hiển thị trong VR)

                        const calloutConfig = (config?.['Call-out'] && typeof config['Call-out'] === 'object') ? config['Call-out'] : {};
                        const calloutHeight = parseNumericValue(calloutConfig.height, 40) ?? 40; // Chiều cao cột callout (pixels)
                        const traitName = `${hotspotName}_trait`;
                        const textHotspotName = `${hotspotName}_callout_text`;
                        const calloutTitle = hotspot.title || '';

                        // 1. Load style cho hotspot icon chính
                        ensureHotspotStyle(krpano, hotspot.style);
                        krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_${hotspot.style}_style)`);

                        // SVG support for callout hotspots
                        if (hotspot.style === 'custom') {
                            const svgConfig = config.svg;
                            const hasSvgId = svgConfig && svgConfig.id;
                            if (hasSvgId) {
                                let svgCode: string | null = null;
                                if (hasSvgId) {
                                    svgCode = getSvgIconById(String(svgConfig.id));
                                }

                                if (svgCode) {
                                    krpano.set(`hotspot[${hotspotName}].visible`, false);
                                    krpano.set(`hotspot[${hotspotName}].enabled`, false);

                                    const svgHotspotName = `${hotspotName}_svg`;
                                    const svgColor = svgConfig.color || '#000000';
                                    const svgBgColor = svgConfig.bgColor || '#ffffff';
                                    const svgBorderRadius = svgConfig.borderRadius || 8;
                                    const svgPadding = svgConfig.padding !== undefined ? svgConfig.padding : 8;
                                    const svgScale = config.scale ?? 1;

                                    const coloredSvg = svgCode.replace(/currentColor/g, svgColor);
                                    const frameHtml = `
                                        <div style="display: flex; align-items: center; justify-content: center; width: 60px; height: 60px; padding: ${svgPadding}px; background-color: ${svgBgColor}; border-radius: ${svgBorderRadius}px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); box-sizing: border-box;">
                                            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: ${svgColor};">
                                                ${coloredSvg}
                                            </div>
                                        </div>
                                    `;

                                    try { krpano.call(`removehotspot(${svgHotspotName})`); } catch (e) { }
                                    krpano.call(`addhotspot(${svgHotspotName})`);
                                    krpano.set(`hotspot[${svgHotspotName}].type`, 'text');
                                    krpano.set(`hotspot[${svgHotspotName}].html`, frameHtml);
                                    krpano.set(`hotspot[${svgHotspotName}].css`, `background: transparent !important; border: none !important; padding: 0 !important; margin: 0 !important;`);
                                    krpano.set(`hotspot[${svgHotspotName}].ath`, hotspot.ath);
                                    krpano.set(`hotspot[${svgHotspotName}].atv`, hotspot.atv);
                                    krpano.set(`hotspot[${svgHotspotName}].oy`, -5);
                                    krpano.set(`hotspot[${svgHotspotName}].edge`, 'bottom');
                                    krpano.set(`hotspot[${svgHotspotName}].scale`, svgScale);
                                    krpano.set(`hotspot[${svgHotspotName}].visible`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].enabled`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].bgcolor`, 'transparent');
                                    krpano.set(`hotspot[${svgHotspotName}].bgalpha`, 0);
                                    krpano.set(`hotspot[${svgHotspotName}].zorder`, 6);

                                    // Enable drag and click for SVG hotspot in Admin callout
                                    krpano.set(`hotspot[${svgHotspotName}].ondown`, "set(dragging,true); draghotspot();");
                                    krpano.set(`hotspot[${svgHotspotName}].onup`, `set(dragging,false); js(window.updateSvgHotspotPositionUpdate('${hotspot.id}'));`);
                                    krpano.set(`hotspot[${svgHotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                                }
                            }
                        }

                        // 2. Tạo cột trait bằng action add_hs có sẵn (có sẵn behavior ẩn/hiện)
                        // add_hs(name, ath, atv, height, oy_offset)
                        krpano.call(`add_hs(${traitName}, ${hotspot.ath}, ${hotspot.atv}, ${calloutHeight}, -50)`);

                        // 3. Tạo text hotspot (thay vì layer) - đặt ở đầu cột
                        // Text được đặt cùng vị trí với hotspot gốc, dùng oy để offset lên đầu cột
                        krpano.call(`addhotspot(${textHotspotName})`);
                        krpano.set(`hotspot[${textHotspotName}].html`, calloutTitle);
                        krpano.set(`hotspot[${textHotspotName}].ath`, hotspot.ath);
                        krpano.set(`hotspot[${textHotspotName}].atv`, hotspot.atv);
                        // Offset lên trên: cột có oy=-20, height=40, scale=1.5 => đỉnh cột ở khoảng oy=-80
                        // Text cần ở ngay trên đỉnh cột
                        krpano.set(`hotspot[${textHotspotName}].oy`, -(20 + calloutHeight * 1.5));
                        krpano.set(`hotspot[${textHotspotName}].edge`, 'bottom');
                        krpano.set(`hotspot[${textHotspotName}].url`, 'textfield.swf');

                        // Áp dụng cấu hình config cho tiêu đề callout
                        const titleCss = buildTitleCss(hotspot, config, true);
                        krpano.set(`hotspot[${textHotspotName}].css`, titleCss);

                        const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds, true);
                        Object.entries(overlaySettings).forEach(([property, value]) => {
                            if (value === undefined || value === null) return;
                            krpano.set(`hotspot[${textHotspotName}].${property}`, value);
                        });

                        krpano.set(`hotspot[${textHotspotName}].vcenter`, 'true');
                        krpano.set(`hotspot[${textHotspotName}].textalign`, 'center');
                        krpano.set(`hotspot[${textHotspotName}].zorder`, 7);
                        krpano.set(`hotspot[${textHotspotName}].enabled`, true);
                        krpano.set(`hotspot[${textHotspotName}].alpha`, 0); // Bắt đầu ẩn như trait

                        // Đồng bộ visibility: text alpha follow trait alpha
                        krpano.set(`hotspot[${textHotspotName}].onloaded`, `asyncloop(true, copy(hotspot[${textHotspotName}].alpha, hotspot[${traitName}].alpha))`);

                        // Apply scale for callout hotspots similar to other styles if config.scale is provided
                        try {
                            const scaleValue = config?.scale;
                            if (scaleValue !== undefined && scaleValue !== null && !Number.isNaN(scaleValue)) {
                                setHotspotScale(hotspotName, scaleValue as number);

                                // Thử delay set scale để đảm bảo loadstyle đã hoàn tất
                                setTimeout(() => {
                                    try {
                                        krpano.set(`hotspot[${hotspotName}].scale`, scaleValue);
                                    } catch (e) { /* no-op */ }
                                }, 100);

                                setTimeout(() => {
                                    try {
                                        krpano.set(`hotspot[${hotspotName}].scale`, scaleValue);
                                    } catch (e) { /* no-op */ }
                                }, 500);
                            }
                        } catch (scaleError) {
                            console.warn(`Error applying scale for callout hotspot ${hotspotName}:`, scaleError);
                        }
                    }

                } catch (styleError) {
                    console.warn(`Failed to load style ${styleName}, trying fallback`);
                    try {
                        if (hotspot.style !== 'custom') {
                            krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_giotnuochinhtron_style)`);
                        }
                    } catch (fallbackError) {
                        console.error(`Failed to load fallback style for hotspot ${hotspotName}`);
                    }
                }

                // Set position
                krpano.set(`hotspot[${hotspotName}].ath`, hotspot.ath);
                krpano.set(`hotspot[${hotspotName}].atv`, hotspot.atv);

                // Set distorted for any distorted hotspot (không hardcode)
                const isFloorDistorted = isValueDistorted(krpano.get(`hotspot[${hotspotName}].distorted`)) ||
                    isValueDistorted((hotspot as any).distorted) ||
                    isValueDistorted(config?.distorted);

                let showtext = hotspot.title || '';
                const titleConfig = (config?.Title && typeof config.Title === 'object') ? config.Title : {};
                if (titleConfig['text-decoration'] === 'underline') {
                    showtext = `<span style="border-bottom: 1px solid;">${showtext}</span>`;
                }

                if (hotspot.style_hover === 'hide-displayname') {
                    krpano.set(`hotspot[${hotspotName}].onhover`, null);
                } else {
                    krpano.set(`hotspot[${hotspotName}].onhover`, 'showtext(' + showtext + '); ');
                }

                krpano.set(`hotspot[${hotspotName}].zorder`, 6);
                krpano.set(`hotspot[${hotspotName}].handcursor`, false); // Disable hand cursor for draggable hotspots
                krpano.set(`hotspot[${hotspotName}].capture`, true); // Capture mouse events to prevent panning

                // hiển thị thông tin hotspot (Cách 1: hiển thị tên cho cả hotspot bám sàn, chỉ ẩn khi style_hover == 'hide-displayname')
                if (hotspot.style_hover != 'callout' && hotspot.style_hover != 'hide-displayname') {
                    krpano.call('addhotspot(' + hotspotName + '_invisible)');
                    krpano.set('hotspot[' + hotspotName + '_invisible].html', showtext);

                    krpano.set('hotspot[' + hotspotName + '_invisible].ath', hotspot.ath);
                    const titleCss = buildTitleCss(hotspot, config);
                    if (hotspot.style == 'custom' && config) {
                        // Check if using SVG mode - check by ID first, then code (consistent with hotspot creation)
                        const svgConfig = config.svg;
                        const hasSvgId = svgConfig && svgConfig.id;
                        const hasSvgCode = svgConfig && svgConfig.code && svgConfig.code.trim().length > 0;
                        const isUsingSvg = hasSvgId || hasSvgCode;

                        krpano.set('hotspot[' + hotspotName + '_invisible].atv', hotspot.atv);
                        krpano.set('hotspot[' + hotspotName + '_invisible].align', 'center');
                        krpano.set('hotspot[' + hotspotName + '_invisible].zorder', 100);
                        krpano.set('hotspot[' + hotspotName + '_invisible].visible', "true");
                        krpano.set('hotspot[' + hotspotName + '_invisible].css', titleCss);
                        // Apply overlay settings cho textfield.swf (width, wordwrap, background...)
                        const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds);
                        applyTitleOverlaySettings(krpano, hotspotName, overlaySettings);

                        if (isUsingSvg) {
                            // SVG mode: text below SVG icon (positive oy)
                            krpano.set(`hotspot[${hotspotName}_invisible].edge`, 'top');
                            krpano.set(`hotspot[${hotspotName}_invisible].oy`, 25); // Below SVG icon
                        } else {
                            // Image mode (custom URL): text position relative to the custom image
                            // Use negative oy to position text inside/above the image tag
                            krpano.set(`hotspot[${hotspotName}_invisible].oy`, `-${config.height || 100}`);
                        }


                    } else {
                        // Apply default settings for non-custom hotspots
                        const titleConfig = config?.Title || {};
                        const hasCustomConfig = config && Object.keys(titleConfig).length > 0;

                        if (!hasCustomConfig) {
                            // Use default CSS and settings when no custom config
                            krpano.set(`hotspot[${hotspotName}_invisible].atv`, hotspot.atv);
                            krpano.set(`hotspot[${hotspotName}_invisible].edge`, 'top'); // Anchor at top of text
                            krpano.set(`hotspot[${hotspotName}_invisible].oy`, 10); // Position below icon with spacing
                            krpano.set(`hotspot[${hotspotName}_invisible].css`, `color:#000000ff; font-family: var(--font-family-project); font-size:12px; font-weight:normal; text-align:center; background-color: white; padding:4px 8px; height:auto; width: auto; min-width: 140px; max-width: 200px; border-radius: 10px; border:solid 1px #F6E493; word-wrap: break-word; overflow-wrap: break-word; white-space: normal; line-height: 1.2;`);
                            krpano.set(`hotspot[${hotspotName}_invisible].txtshadow`, '2 2 60 0x014689 4.5');
                            krpano.set(`hotspot[${hotspotName}_invisible].background`, 'true');
                            krpano.set(`hotspot[${hotspotName}_invisible].backgroundalpha`, '1');
                            krpano.set(`hotspot[${hotspotName}_invisible].bgroundedge`, '8');
                            krpano.set(`hotspot[${hotspotName}_invisible].border`, 'true');
                            krpano.set(`hotspot[${hotspotName}_invisible].bordercolor`, '0xf6e493');
                            krpano.set(`hotspot[${hotspotName}_invisible].borderwidth`, '1.2');
                            krpano.set(`hotspot[${hotspotName}_invisible].roundedge`, '8');
                            krpano.set(`hotspot[${hotspotName}_invisible].zorder`, 7);
                            krpano.set(`hotspot[${hotspotName}_invisible].enabled`, true);
                        } else {
                            // Use custom config
                            krpano.set('hotspot[' + hotspotName + '_invisible].atv', hotspot.atv);
                            krpano.set('hotspot[' + hotspotName + '_invisible].edge', 'top'); // Anchor at top of text
                            krpano.set('hotspot[' + hotspotName + '_invisible].oy', 10); // Position below icon with spacing
                            krpano.set('hotspot[' + hotspotName + '_invisible].css', titleCss);
                            const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds);
                            applyTitleOverlaySettings(krpano, hotspotName, overlaySettings);
                        }
                    }





                    krpano.set('hotspot[' + hotspotName + '_invisible].url', 'textfield.swf');
                    krpano.set('hotspot[' + hotspotName + '_invisible].vcenter', 'true');
                    krpano.set('hotspot[' + hotspotName + '_invisible].textalign', 'center');
                    krpano.set(`hotspot[${hotspotName}_invisible].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                } else if (hotspot.style_hover === 'hide-displayname') {
                    try { krpano.call(`removehotspot(${hotspotName}_invisible)`); } catch (e) { }
                    try { krpano.call(`removelayer(${hotspotName}_text)`); } catch (e) { }
                }

                if (isFloorDistorted) {
                    krpano.set(`hotspot[${hotspotName}].distorted`, true);
                    const targetRx = config?.rx !== undefined && config?.rx !== ''
                        ? Number(config.rx)
                        : (krpano.get(`hotspot[${hotspotName}].rx`) || -60);
                    krpano.set(`hotspot[${hotspotName}].rx`, targetRx);
                    // Ẩn vạch trait chân cột nếu có cho icon nằm sàn
                    try { krpano.call(`removehotspot(${hotspotName}_trait)`); } catch (e) { }
                }

                // Set onclick to navigate to target scene (similar to original)
                // REMOVED: Navigation onclick - now handled by popup
                // if (hotspot.target_scene_name || hotspot.link) {
                //     const targetScene = hotspot.target_scene_name || hotspot.link;
                //     krpano.set(`hotspot[${hotspotName}].linkedscene`, targetScene);
                //     krpano.set(`hotspot[${hotspotName}].onclick`, `loadscene(${targetScene}, null, MERGE, BLEND(1))`);
                // }

                // Set onclick to show popup instead of navigation
                krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);

                // Set tooltip (similar to original displayname logic)
                if (hotspot.title) {
                    krpano.set(`hotspot[${hotspotName}].tooltip`, hotspot.title);
                }

                // Set drag functionality (similar to original)
                krpano.set(`hotspot[${hotspotName}].ondown`, "set(dragging,true); draghotspot();");
                krpano.set(`hotspot[${hotspotName}].onup`, `set(dragging,false); js('window.updatePosition("${hotspotName}",${hotspot.id})')`);


            } catch (error) {
                console.error(`Error adding hotspot ${hotspotName}:`, error);
            }
        });

        // Update current scene hotspots for UI
        setCurrentSceneHotspots(sceneHotspots.filter(hotspot => hotspot.is_show === 1));

    };
    const isTextWidthExceeds = useCallback((text: string, font: string = '12px Montserrat', maxWidth: number = 200): boolean => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return false;

        context.font = font;
        const textWidth = context.measureText(text).width;

        return textWidth > maxWidth;
    }, []);

    // Monitor scene changes and load hotspots for new scene
    useEffect(() => {
        if (!krpano) return;

        // Get current scene name
        const getCurrentSceneName = () => {
            return krpanoCurrent?.scene_name || (krpano.get("xml.scene") as string) || '';
        };

        const currentSceneName = getCurrentSceneName();

        // Load hotspots by scene when scene changed
        if (currentSceneName && (currentSceneName !== lastScene || idPath !== lastLoadedIdPath)) {
            setLastScene(currentSceneName);
            setLastLoadedIdPath(idPath);
            loadHotspotsByScene(currentSceneName);
        }

        // Poll for scene changes (fallback method)
        const interval = setInterval(() => {
            const newSceneName = getCurrentSceneName();
            if (newSceneName && (newSceneName !== lastScene || idPath !== lastLoadedIdPath)) {
                setLastScene(newSceneName);
                setLastLoadedIdPath(idPath);
                loadHotspotsByScene(newSceneName);
            }
        }, 1000);

        return () => clearInterval(interval);

    }, [krpano, krpanoCurrent?.scene_name, lastScene, idPath, lastLoadedIdPath]);

    // Monitor hotspots changes and add them to krpano when loaded
    useEffect(() => {
        if (!krpano || !Array.isArray(hotspots) || hotspots.length === 0) {
            sceneRenderPending.current = false;
            return;
        }

        const onlyScaleChanged = scaleOnlyHotspots.current === hotspots;
        scaleOnlyHotspots.current = null;
        if (onlyScaleChanged && !sceneRenderPending.current) return;

        const currentSceneName = krpanoCurrent?.scene_name || (krpano.get("xml.scene") as string) || '';
        if (currentSceneName) {
            // Delay adding hotspots to ensure Krpano's loadscene blend transition (500ms) has fully finished.
            // Hotspots added during the transition get wiped by Krpano when the new scene becomes fully active.
            sceneRenderPending.current = true;
            const timeoutId = setTimeout(() => {
                sceneRenderPending.current = false;
                addHotspotsForCurrentScene(currentSceneName);
            }, 600);
            return () => clearTimeout(timeoutId);
        }

    }, [krpano, hotspots]);

    // Helper functions for managing hotspots
    const addHotspotToScene = (hotspot: APIHotspot) => {
        setHotspots(prev => {
            const currentHotspots = Array.isArray(prev) ? prev : [];
            const newHotspots = [...currentHotspots, hotspot];

            // Add hotspot directly to krpano immediately (không cần đợi state update)
            if (krpano) {
                const hotspotName = `api_${hotspot.id}`;

                try {

                    // Add hotspot to krpano
                    krpano.call(`addhotspot(${hotspotName})`);

                    // Parse config for custom hotspots
                    let config = navigationHotspotRenderConfig(normalizeHotspotConfig(hotspot.config), krpano.get('device.mobile'));

                    // For custom hotspots, ensure default config values
                    if (hotspot.style === 'custom') {
                        config = {
                            url: config.url || '',
                            scale: config.scale ?? 0.3,
                            height: config.height || 100,
                            ...config
                        };
                    }

                    // Load style based on hotspot.style (same logic as addHotspotsForCurrentScene)
                    const styleMapping: Record<string, string> = {
                        'default': 'hotspot_giotnuochinhtron_style',
                        'door': 'hotspot_giotnuochinhtron_style',
                        'video360gif': 'hotspot_giotnuochinhtron_style',
                        'vongtron': 'hotspot_giotnuochinhtron_style',
                        'khinhkhicau': 'hotspot_khinhkhicau_style',
                        'thienvien': 'hotspot_giotnuochinhtron_style',
                        'lotus3d': 'hotspot_giotnuochinhtron_style',
                        'chimhac': 'hotspot_giotnuochinhtron_style',
                        'daukhi': 'hotspot_giotnuochinhtron_style',
                        'svg': 'hotspot_giotnuochinhtron_style',
                        'giotnuoc': 'hotspot_giotnuochinhtron_style',
                        'giotnuochinhtron': 'hotspot_giotnuochinhtron_style',
                        'lotus': 'hotspot_giotnuochinhtron_style',
                        'location': 'hotspot_giotnuochinhtron_style',
                        'BTLS': 'hotspot_giotnuochinhtron_style',
                        'flycam': 'hotspot_giotnuochinhtron_style',
                        'video_time': 'hotspot_giotnuochinhtron_style',
                        'btpnnb': 'hotspot_giotnuochinhtron_style',
                        'image2d': 'hotspot_giotnuochinhtron_style'
                    };

                    let styleName = styleMapping[hotspot.style] || 'hotspot_giotnuochinhtron_style';

                    // Handle show/hide logic
                    if (hotspot.is_show !== 1) {
                        if (styleName.includes('_style')) {
                            styleName = styleName.replace('_style', '_hidden_style');
                        } else {
                            styleName = 'hotspot_default_hidden_style';
                        }
                    }


                    // Try to load style, fallback to default if failed
                    try {
                        // If the hotspot style produces a callout (callout hover), create it with helper
                        if (hotspot.style_hover === 'callout') {
                            const calloutConfig = (config?.['Call-out'] && typeof config['Call-out'] === 'object') ? config['Call-out'] : {};
                            const calloutHeight = parseNumericValue(calloutConfig.height, 40) ?? 40;
                            const traitName = `${hotspotName}_trait`;
                            const textHotspotName = `${hotspotName}_callout_text`;
                            const calloutTitle = hotspot.title || '';

                            // 1. Load style cho hotspot icon chính
                            ensureHotspotStyle(krpano, hotspot.style);
                            krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_${hotspot.style}_style)`);

                            // SVG support for callout hotspots in addHotspotToScene
                            if (hotspot.style === 'custom') {
                                const svgConfig = config.svg;
                                const hasSvgId = svgConfig && svgConfig.id;
                                if (hasSvgId) {
                                    let svgCode: string | null = null;
                                    if (hasSvgId) {
                                        svgCode = getSvgIconById(String(svgConfig.id));
                                    }

                                    if (svgCode) {
                                        krpano.set(`hotspot[${hotspotName}].visible`, false);
                                        krpano.set(`hotspot[${hotspotName}].enabled`, false);

                                        const svgHotspotName = `${hotspotName}_svg`;
                                        const svgColor = svgConfig.color || '#000000';
                                        const svgBgColor = svgConfig.bgColor || '#ffffff';
                                        const svgBorderRadius = svgConfig.borderRadius || 8;
                                        const svgPadding = svgConfig.padding !== undefined ? svgConfig.padding : 8;
                                        const svgScale = config.scale ?? 1;

                                        const coloredSvg = svgCode.replace(/currentColor/g, svgColor);
                                        const frameHtml = `
                                            <div style="display: flex; align-items: center; justify-content: center; width: 60px; height: 60px; padding: ${svgPadding}px; background-color: ${svgBgColor}; border-radius: ${svgBorderRadius}px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); box-sizing: border-box;">
                                                <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: ${svgColor};">
                                                    ${coloredSvg}
                                                </div>
                                            </div>
                                        `;

                                        try { krpano.call(`removehotspot(${svgHotspotName})`); } catch (e) { }
                                        krpano.call(`addhotspot(${svgHotspotName})`);
                                        krpano.set(`hotspot[${svgHotspotName}].type`, 'text');
                                        krpano.set(`hotspot[${svgHotspotName}].html`, frameHtml);
                                        krpano.set(`hotspot[${svgHotspotName}].css`, `background: transparent !important; border: none !important; padding: 0 !important; margin: 0 !important;`);
                                        krpano.set(`hotspot[${svgHotspotName}].ath`, hotspot.ath);
                                        krpano.set(`hotspot[${svgHotspotName}].atv`, hotspot.atv);
                                        krpano.set(`hotspot[${svgHotspotName}].oy`, -5);
                                        krpano.set(`hotspot[${svgHotspotName}].edge`, 'bottom');
                                        krpano.set(`hotspot[${svgHotspotName}].scale`, svgScale);
                                        krpano.set(`hotspot[${svgHotspotName}].visible`, true);
                                        krpano.set(`hotspot[${svgHotspotName}].enabled`, true);
                                        krpano.set(`hotspot[${svgHotspotName}].bgcolor`, 'transparent');
                                        krpano.set(`hotspot[${svgHotspotName}].bgalpha`, 0);
                                        krpano.set(`hotspot[${svgHotspotName}].zorder`, 6);

                                        // Enable drag and click for SVG hotspot in Admin addHotspotToScene
                                        krpano.set(`hotspot[${svgHotspotName}].ondown`, "set(dragging,true); draghotspot();");
                                        krpano.set(`hotspot[${svgHotspotName}].onup`, `set(dragging,false); js(window.updateSvgHotspotPositionApi('${hotspot.id}'));`);
                                        krpano.set(`hotspot[${svgHotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                                    }
                                }
                            }

                            // 2. Tạo cột trait bằng action add_hs
                            krpano.call(`add_hs(${traitName}, ${hotspot.ath}, ${hotspot.atv}, ${calloutHeight}, -50)`);

                            // 3. Tạo text hotspot
                            krpano.call(`addhotspot(${textHotspotName})`);
                            krpano.set(`hotspot[${textHotspotName}].html`, calloutTitle);
                            krpano.set(`hotspot[${textHotspotName}].ath`, hotspot.ath);
                            krpano.set(`hotspot[${textHotspotName}].atv`, hotspot.atv);
                            krpano.set(`hotspot[${textHotspotName}].oy`, -(20 + calloutHeight * 1.5));
                            krpano.set(`hotspot[${textHotspotName}].edge`, 'bottom');
                            krpano.set(`hotspot[${textHotspotName}].url`, 'textfield.swf');

                            // Áp dụng cấu hình config cho tiêu đề callout
                            const titleCss = buildTitleCss(hotspot, config, true);
                            krpano.set(`hotspot[${textHotspotName}].css`, titleCss);

                            const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds, true);
                            Object.entries(overlaySettings).forEach(([property, value]) => {
                                if (value === undefined || value === null) return;
                                krpano.set(`hotspot[${textHotspotName}].${property}`, value);
                            });

                            krpano.set(`hotspot[${textHotspotName}].vcenter`, 'true');
                            krpano.set(`hotspot[${textHotspotName}].textalign`, 'center');
                            krpano.set(`hotspot[${textHotspotName}].zorder`, 7);
                            krpano.set(`hotspot[${textHotspotName}].enabled`, true);
                            krpano.set(`hotspot[${textHotspotName}].alpha`, 0);

                            // Đồng bộ visibility
                            krpano.set(`hotspot[${textHotspotName}].onloaded`, `asyncloop(true, copy(hotspot[${textHotspotName}].alpha, hotspot[${traitName}].alpha))`);

                            // Apply scale for callout if present
                            try {
                                const scaleValue = config?.scale ?? (hotspot.style === 'custom' ? (config?.scale ?? 1.0) : undefined);
                                if (scaleValue !== undefined && scaleValue !== null) {
                                    setHotspotScale(hotspotName, scaleValue as number);
                                }
                            } catch (e) { /* no-op */ }
                        } else if (hotspot.style == 'custom' && config) {
                            // Check if using SVG icon in addHotspotToScene (regular custom)
                            const svgConfig = config.svg;
                            const hasSvgId = svgConfig && svgConfig.id;
                            const isUsingSvg = Boolean(hasSvgId);

                            if (isUsingSvg) {
                                let svgCode: string | null = null;
                                if (hasSvgId) {
                                    svgCode = getSvgIconById(String(svgConfig.id));
                                }

                                if (svgCode) {
                                    // SVG mode: hide main hotspot icon, show SVG frame instead
                                    krpano.set(`hotspot[${hotspotName}].visible`, false);
                                    krpano.set(`hotspot[${hotspotName}].enabled`, false);

                                    // Create SVG hotspot
                                    const svgHotspotName = `${hotspotName}_svg`;

                                    const svgColor = svgConfig.color || '#000000';
                                    const svgBgColor = svgConfig.bgColor || '#ffffff';
                                    const svgBorderRadius = svgConfig.borderRadius || 8;
                                    const svgPadding = svgConfig.padding !== undefined ? svgConfig.padding : 8;
                                    const svgScale = config.scale ?? 1;

                                    // Build SVG icon with custom colors
                                    const coloredSvg = svgCode.replace(/currentColor/g, svgColor);

                                    // Create HTML frame with SVG icon
                                    const frameHtml = `
                                        <div style="display: flex; align-items: center; justify-content: center; width: 60px; height: 60px; padding: ${svgPadding}px; background-color: ${svgBgColor}; border-radius: ${svgBorderRadius}px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); box-sizing: border-box;">
                                            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: ${svgColor};">
                                                ${coloredSvg}
                                            </div>
                                        </div>
                                    `;

                                    // Remove old SVG hotspot if exists
                                    try { krpano.call(`removehotspot(${svgHotspotName})`); } catch (e) { /* ignore */ }

                                    // Create SVG HTML hotspot
                                    krpano.call(`addhotspot(${svgHotspotName})`);
                                    krpano.set(`hotspot[${svgHotspotName}].type`, 'text');
                                    krpano.set(`hotspot[${svgHotspotName}].html`, frameHtml);
                                    krpano.set(`hotspot[${svgHotspotName}].css`, `background: transparent !important; border: none !important; padding: 0 !important; margin: 0 !important;`);
                                    krpano.set(`hotspot[${svgHotspotName}].ath`, hotspot.ath);
                                    krpano.set(`hotspot[${svgHotspotName}].atv`, hotspot.atv);
                                    krpano.set(`hotspot[${svgHotspotName}].oy`, -5);
                                    krpano.set(`hotspot[${svgHotspotName}].edge`, 'bottom');
                                    krpano.set(`hotspot[${svgHotspotName}].scale`, svgScale);
                                    krpano.set(`hotspot[${svgHotspotName}].visible`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].enabled`, true);
                                    krpano.set(`hotspot[${svgHotspotName}].bgcolor`, 'transparent');
                                    krpano.set(`hotspot[${svgHotspotName}].bgalpha`, 0);
                                    krpano.set(`hotspot[${svgHotspotName}].zorder`, 6);

                                    // Enable drag for SVG hotspot
                                    krpano.set(`hotspot[${svgHotspotName}].ondown`, "set(dragging,true); draghotspot();");
                                    krpano.set(`hotspot[${svgHotspotName}].onup`, `set(dragging,false); js(window.updateSvgHotspotPositionApi('${hotspot.id}'));`);

                                    // Set onclick to show popup
                                    krpano.set(`hotspot[${svgHotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                                } else {
                                    console.warn(`[HotspotManager] Could not load SVG for hotspot ${hotspotName} with ID ${svgConfig.id}`);
                                    krpano.set(`hotspot[${hotspotName}].url`, config.url);
                                }
                            } else {
                                krpano.set(`hotspot[${hotspotName}].url`, config.url);
                                setHotspotScale(hotspotName, config.scale ?? 1.0);
                                krpano.set(`hotspot[${hotspotName}].align`, "center");
                                krpano.set(`hotspot[${hotspotName}].zorder`, 0);
                                const customHeight = typeof config.height === 'number'
                                    ? config.height
                                    : (config.height !== undefined && config.height !== null && config.height !== '' ? parseInt(String(config.height), 10) : 100);

                                if (config.distorted || customHeight === 0) {
                                    krpano.set(`hotspot[${hotspotName}].distorted`, true);
                                    krpano.set(`hotspot[${hotspotName}].rx`, config.rx !== undefined ? Number(config.rx) : -60);
                                    krpano.set(`hotspot[${hotspotName}].edge`, 'center');
                                    krpano.set(`hotspot[${hotspotName}].oy`, 0);
                                } else {
                                    krpano.set(`hotspot[${hotspotName}].oy`, `-${customHeight}`);
                                }
                            }
                        } else {
                            // Load style first
                            ensureHotspotStyle(krpano, hotspot.style);
                            krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_${hotspot.style}_style)`);

                            // Only override scale if explicitly defined in config
                            if (config.scale !== undefined && config.scale !== null) {
                                const scaleValue = config.scale;

                                // Use robust helper to apply scale
                                setHotspotScale(hotspotName, scaleValue);
                            }
                        }
                    } catch (styleError) {
                        console.warn(`Failed to load style ${styleName}, trying fallback`);
                        try {
                            if (hotspot.style !== 'custom') {
                                krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_giotnuochinhtron_style)`);
                            }
                        } catch (fallbackError) {
                            console.error(`Failed to load fallback style for hotspot ${hotspotName}`);
                        }
                    }

                    // Set position
                    krpano.set(`hotspot[${hotspotName}].ath`, hotspot.ath);
                    krpano.set(`hotspot[${hotspotName}].atv`, hotspot.atv);

                    // Set distorted for any distorted hotspot (không hardcode)
                    const isFloorDistorted = isValueDistorted(krpano.get(`hotspot[${hotspotName}].distorted`)) ||
                        isValueDistorted((hotspot as any).distorted) ||
                        isValueDistorted(config?.distorted);

                    const showtext = hotspot.title;
                    if (hotspot.style_hover === 'hide-displayname') {
                        krpano.set(`hotspot[${hotspotName}].onhover`, null);
                    } else {
                        krpano.set(`hotspot[${hotspotName}].onhover`, 'showtext(' + showtext + '); ');
                    }

                    krpano.set(`hotspot[${hotspotName}].zorder`, 6);
                    krpano.set(`hotspot[${hotspotName}].handcursor`, false); // Disable hand cursor for draggable hotspots
                    krpano.set(`hotspot[${hotspotName}].capture`, true); // Capture mouse events to prevent panning

                    // hiển thị thông tin hotspot (Cách 1: hiển thị tên cho cả hotspot bám sàn, chỉ ẩn khi style_hover == 'hide-displayname')
                    if (hotspot.style_hover != 'callout' && hotspot.style_hover != 'hide-displayname') {
                        krpano.call('addhotspot(' + hotspotName + '_invisible)');
                        krpano.set('hotspot[' + hotspotName + '_invisible].html', showtext);

                        krpano.set('hotspot[' + hotspotName + '_invisible].ath', hotspot.ath);
                        const titleCss = buildTitleCss(hotspot, config);
                        if (hotspot.style == 'custom' && config) {
                            krpano.set('hotspot[' + hotspotName + '_invisible].atv', hotspot.atv);
                            krpano.set('hotspot[' + hotspotName + '_invisible].align', 'center');
                            krpano.set('hotspot[' + hotspotName + '_invisible].zorder', 100);
                            krpano.set('hotspot[' + hotspotName + '_invisible].visible', "true");
                            krpano.set('hotspot[' + hotspotName + '_invisible].css', titleCss);
                            // Apply overlay settings cho textfield.swf (width, wordwrap, background...)
                            const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds);
                            applyTitleOverlaySettings(krpano, hotspotName, overlaySettings);

                            // Check if using SVG mode in addHotspotToScene
                            const svgConfig = config.svg;
                            const isUsingSvg = (svgConfig && svgConfig.id) || (svgConfig && svgConfig.code && svgConfig.code.trim().length > 0);

                            if (isUsingSvg) {
                                krpano.set(`hotspot[${hotspotName}_invisible].edge`, 'top');
                                krpano.set(`hotspot[${hotspotName}_invisible].oy`, 25); // Below SVG icon
                            } else {
                                // Image mode: text below icon
                                krpano.set(`hotspot[${hotspotName}_invisible].edge`, 'top');
                                krpano.set(`hotspot[${hotspotName}_invisible].oy`, 25);
                            }

                            krpano.call('addhotspot(' + hotspotName + '_trait)');
                            krpano.set('hotspot[' + hotspotName + '_trait].url', 'https://sanpham.starglobal3d.com/managements/js/plugins/hs/trait.png');
                            krpano.set('hotspot[' + hotspotName + '_trait].align', 'bottom');
                            krpano.set('hotspot[' + hotspotName + '_trait].edge', 'bottom');
                            krpano.set('hotspot[' + hotspotName + '_trait].ath', hotspot.ath);
                            krpano.set('hotspot[' + hotspotName + '_trait].atv', hotspot.atv);
                            krpano.set('hotspot[' + hotspotName + '_trait].height', config.height);

                        } else {
                            // Apply default settings for non-custom hotspots
                            const titleConfig = config?.Title || {};
                            const hasCustomConfig = config && Object.keys(titleConfig).length > 0;

                            if (!hasCustomConfig) {
                                // Use default CSS and settings when no custom config
                                krpano.set(`hotspot[${hotspotName}_invisible].atv`, hotspot.atv);
                                krpano.set(`hotspot[${hotspotName}_invisible].edge`, 'top'); // Anchor at top of text
                                krpano.set(`hotspot[${hotspotName}_invisible].oy`, 35); // Position below icon with spacing
                                krpano.set(`hotspot[${hotspotName}_invisible].css`, `color:#000000ff; font-family: var(--font-family-project); font-size:12px; font-weight:normal; text-align:center; background-color: white; padding:4px 8px; height:auto; width: auto; max-width: 200px; border-radius: 10px; border:solid 1px #F6E493; word-wrap: break-word; overflow-wrap: break-word; white-space: normal; line-height: 1.2;`);
                                krpano.set(`hotspot[${hotspotName}_invisible].txtshadow`, '2 2 60 0x014689 4.5');
                                krpano.set(`hotspot[${hotspotName}_invisible].background`, 'true');
                                krpano.set(`hotspot[${hotspotName}_invisible].backgroundalpha`, '1');
                                krpano.set(`hotspot[${hotspotName}_invisible].bgroundedge`, '8');
                                krpano.set(`hotspot[${hotspotName}_invisible].border`, 'true');
                                krpano.set(`hotspot[${hotspotName}_invisible].bordercolor`, '0xf6e493');
                                krpano.set(`hotspot[${hotspotName}_invisible].borderwidth`, '3.0');
                                krpano.set(`hotspot[${hotspotName}_invisible].roundedge`, '8');
                                krpano.set(`hotspot[${hotspotName}_invisible].zorder`, 7);
                                krpano.set(`hotspot[${hotspotName}_invisible].enabled`, true);
                            } else {
                                // Use custom config
                                krpano.set('hotspot[' + hotspotName + '_invisible].atv', hotspot.atv);
                                krpano.set('hotspot[' + hotspotName + '_invisible].edge', 'top'); // Anchor at top of text
                                krpano.set('hotspot[' + hotspotName + '_invisible].oy', 35); // Position below icon with spacing
                                krpano.set('hotspot[' + hotspotName + '_invisible].css', titleCss);
                                const overlaySettings = getTitleOverlaySettings(hotspot, config, isTextWidthExceeds);
                                applyTitleOverlaySettings(krpano, hotspotName, overlaySettings);
                            }
                        }

                        krpano.set('hotspot[' + hotspotName + '_invisible].url', 'textfield.swf');
                        krpano.set('hotspot[' + hotspotName + '_invisible].vcenter', 'true');
                        krpano.set('hotspot[' + hotspotName + '_invisible].textalign', 'center');
                        krpano.set(`hotspot[${hotspotName}_invisible].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);
                    } else if (hotspot.style_hover === 'hide-displayname') {
                        try { krpano.call(`removehotspot(${hotspotName}_invisible)`); } catch (e) { }
                        try { krpano.call(`removelayer(${hotspotName}_text)`); } catch (e) { }
                    }

                    if (isFloorDistorted) {
                        krpano.set(`hotspot[${hotspotName}].distorted`, true);
                        const targetRx = config?.rx !== undefined && config?.rx !== ''
                            ? Number(config.rx)
                            : (krpano.get(`hotspot[${hotspotName}].rx`) || -60);
                        krpano.set(`hotspot[${hotspotName}].rx`, targetRx);
                        // Ẩn vạch trait chân cột nếu có cho icon nằm sàn
                        try { krpano.call(`removehotspot(${hotspotName}_trait)`); } catch (e) { }
                    }

                    // Set onclick to show popup instead of navigation
                    krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showHotspotPopup('${hotspot.id}', get(mouse.stagex), get(mouse.stagey)))`);

                    // Set tooltip
                    if (hotspot.title) {
                        krpano.set(`hotspot[${hotspotName}].tooltip`, hotspot.title);
                    }

                    // Set drag functionality
                    krpano.set(`hotspot[${hotspotName}].ondown`, "set(dragging,true); draghotspot();");
                    krpano.set(`hotspot[${hotspotName}].onup`, `set(dragging,false); js('window.updatePosition("${hotspotName}",${hotspot.id})')`);


                } catch (error) {
                    console.error(`Error adding hotspot ${hotspotName} to krpano:`, error);
                }
            }

            return newHotspots;
        });

        // Update currentSceneHotspots if this hotspot belongs to current scene
        const currentSceneName = krpanoCurrent?.scene_name || (krpano && krpano.get("xml.scene") as string) || '';
        if (hotspot.scene_name?.toLowerCase() === currentSceneName.toLowerCase() && hotspot.is_show === 1) {
            setCurrentSceneHotspots(prev => [...prev, hotspot]);
        }
    };

    const removeHotspotFromScene = (hotspotId: number) => {

        setHotspots(prev => {
            const currentHotspots = Array.isArray(prev) ? prev : [];

            // Remove from krpano (use api_ prefix to match the add logic)
            if (krpano) {
                const hotspotName = `api_${hotspotId}`;
                try {
                    krpano.call(`removehotspot(${hotspotName})`);
                    krpano.call(`removehotspot(${hotspotName}_trait)`);
                    krpano.call(`removehotspot(${hotspotName}_invisible)`);
                    krpano.call(`removehotspot(${hotspotName}_svg)`);
                    krpano.call(`removehotspot(${hotspotName}_callout_text)`);
                } catch (error) {
                    console.warn(`⚠️ Error removing hotspot ${hotspotName} from krpano:`, error);
                }
            }

            const filteredHotspots = currentHotspots.filter(h => h.id !== hotspotId);

            return filteredHotspots;
        });

        // Also remove from currentSceneHotspots
        setCurrentSceneHotspots(prev => {
            const filtered = prev.filter(h => h.id !== hotspotId);
            return filtered;
        });
    };

    // Update hotspot properties in krpano only
    const updateHotspotInKrpano = (hotspot: APIHotspot) => {
        if (!krpano) {
            return;
        }

        const hotspotName = `api_${hotspot.id}`;

        try {
            // Use krpano.set to update individual properties
            krpano.set(`hotspot[${hotspotName}].ath`, hotspot.ath);
            krpano.set(`hotspot[${hotspotName}].atv`, hotspot.atv);
            krpano.set(`hotspot[${hotspotName}].style`, hotspot.style);
            krpano.set(`hotspot[${hotspotName}].tooltip`, hotspot.title);

            // Update visibility
            if (hotspot.is_show !== undefined) {
                krpano.set(`hotspot[${hotspotName}].visible`, hotspot.is_show === 1 ? true : false);
            }

            // Set handcursor to false to avoid confusion
            krpano.set(`hotspot[${hotspotName}].handcursor`, false);

            // IMPORTANT: Always re-set drag handlers regardless of style
            // This ensures hotspots remain draggable even after style changes
            krpano.call(`hotspot[${hotspotName}].ondown = set(dragging,true); set(control.mouse,off); draghotspot();`);
            krpano.call(`hotspot[${hotspotName}].onup = set(dragging,false); set(control.mouse,on);`);

        } catch (error) {
            console.error(`Error updating hotspot ${hotspotName} in krpano:`, error);
        }
    };

    const refreshHotspots = async () => {
        const currentSceneName = krpanoCurrent?.scene_name || (krpano && krpano.get("xml.scene") as string) || '';
        if (currentSceneName) {
            await loadHotspotsByScene(currentSceneName); // ← Sử dụng loadHotspotsByScene thay vì loadAllHotspots
            // Note: addHotspotsForCurrentScene is automatically called by useEffect when hotspots state updates
        }
    };

    // Load hotspots when component mounts - loads by current scene
    useEffect(() => {
        const currentSceneName = krpanoCurrent?.scene_name || (krpano && krpano.get("xml.scene") as string) || '';
        if (currentSceneName) {
            loadHotspotsByScene(currentSceneName); // ← Chỉ load theo scene
        }
    }, []);

    // Popup management functions
    const showPopup = (hotspot: APIHotspot, position: { x: number; y: number }) => {
        setPopupHotspot(hotspot);
        setPopupPosition(position);
        setIsPopupVisible(true);
    };

    const hidePopup = () => {
        setPopupHotspot(null);
        setPopupPosition(null);
        setIsPopupVisible(false);
    };

    // Update form management functions
    const openUpdateForm = (hotspot: APIHotspot) => {
        setUpdateFormHotspot(hotspot);
        setIsUpdateFormOpen(true);
        hidePopup(); // Close popup when opening form
    };

    const closeUpdateForm = () => {
        setUpdateFormHotspot(null);
        setIsUpdateFormOpen(false);
    };

    // Trong HotspotManager.tsx, sửa function showHotspotPopup:
    useEffect(() => {
        (window as any).showHotspotPopup = (hotspotId: string, x: number, y: number) => {

            // Tìm hotspot
            const hotspot = hotspots.find(h => h.id.toString() === hotspotId);

            if (hotspot) {
                // Tìm container krpano - thử nhiều selector khác nhau
                const krpanoElement = document.getElementById('krpanoSWFObject') ||
                    document.querySelector('#pano') ||
                    document.querySelector('.krpano-container') ||
                    document.querySelector('div[id*="krpano"]');


                let adjustedX = x;
                let adjustedY = y;

                if (krpanoElement) {
                    const rect = krpanoElement.getBoundingClientRect();

                    // Chuyển đổi tọa độ krpano sang tọa độ viewport
                    adjustedX = rect.left + x;
                    adjustedY = rect.top + y;
                } else {
                    console.warn('Krpano element not found, using raw coordinates');
                    // Fallback: sử dụng tọa độ trực tiếp nhưng điều chỉnh từ center
                    adjustedX = window.innerWidth / 2 + x;
                    adjustedY = window.innerHeight / 2 + y;
                }

                const finalPosition = adjustPopupPosition(adjustedX, adjustedY);

                // Force update states
                setPopupHotspot(hotspot);
                setPopupPosition(finalPosition);
                setIsPopupVisible(true);
            } else {
                console.warn(`Hotspot with id ${hotspotId} not found in`, hotspots);
            }
        };

        return () => {
            delete (window as any).showHotspotPopup;
        };
    }, [hotspots]);

    // Cập nhật function adjustPopupPosition để có offset tốt hơn:
    const adjustPopupPosition = (x: number, y: number) => {
        const popupWidth = 200;
        const popupHeight = 120;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const offset = 20; // Khoảng cách từ hotspot đến popup

        // Xác định popup nên hiện bên trái hay bên phải
        let adjustedX = x + offset;
        let adjustedY = y;

        // Nếu popup vượt ra ngoài màn hình bên phải, hiện bên trái
        if (adjustedX + popupWidth > viewportWidth - 10) {
            adjustedX = x - popupWidth - offset;
        }

        // Đảm bảo popup không vượt ra ngoài màn hình bên trái
        if (adjustedX < 10) {
            adjustedX = 10;
        }

        // Đảm bảo popup không vượt ra ngoài màn hình trên/dưới
        if (adjustedY - popupHeight / 2 < 10) {
            adjustedY = 10 + popupHeight / 2;
        } else if (adjustedY + popupHeight / 2 > viewportHeight - 10) {
            adjustedY = viewportHeight - 10 - popupHeight / 2;
        }

        const result = { x: adjustedX, y: adjustedY };

        return result;
    };

    return (
        <HotspotManagerContext.Provider value={{
            hotspots,
            currentSceneHotspots,
            loadAllHotspots,
            loadHotspotsByScene,
            addHotspotToScene,
            removeHotspotFromScene,
            updateHotspotInKrpano,
            refreshHotspots,
            isLoading,
            popupHotspot,
            popupPosition,
            showPopup,
            hidePopup,
            isPopupVisible,
            openUpdateForm,
            updateFormHotspot,
            isUpdateFormOpen,
            closeUpdateForm
        }}>
            {children}
        </HotspotManagerContext.Provider>
    );
};

export const useHotspotManager = () => {
    const context = useContext(HotspotManagerContext);
    if (context === undefined) {
        throw new Error('useHotspotManager must be used within a HotspotManagerProvider');
    }
    return context;
};
